import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { refreshToken } from '../Api';
import { clearAuthCookies } from '../utils/cookieUtils';
import ROUTES from '../routes';
import socketService from '../Services/SocketService';
import { logSignalR } from '../utils/SignalRUtils';

const ChatContext = createContext();

export const SignalRProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  const connectionRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const navigate = useNavigate();

  // Move console logs to useEffect to avoid render-time side effects
  useEffect(() => {
    logSignalR("=== SignalRProvider rendered ===");
    logSignalR("=== SignalRProvider rendered at:", new Date().toISOString());
    logSignalR("SignalRProvider state - connection:", connection);
    logSignalR("SignalRProvider state - access token:", !!Cookies.get("AccessToken"));
    logSignalR("=== FORCING CONNECTION START ===");
  }, [connection]);
  
  const startPingMechanism = useCallback((connection) => {
    // Clear any existing ping interval
    clearInterval(pingIntervalRef.current);
    
    // Send a ping every 10 seconds to keep the connection alive
    pingIntervalRef.current = setInterval(() => {
      if (connection && connection.state === signalR.HubConnectionState.Connected) {
        // Try to send a heartbeat message to keep the connection alive
        // If the server doesn't have this method, it will fail silently
        connection.invoke("Heartbeat")
          .catch((error) => {
            // Only log if it's not a "Method does not exist" error
            if (!error.message.includes("Method does not exist")) {
              logSignalR("Heartbeat failed:", error);
            }
          });
      }
    }, 10000); // 10 seconds
  }, []);

  const startConnection = useCallback(async () => {
    logSignalR("=== startConnection called ===");
    
    // Simple check - if we already have a connection, don't start another
    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
      logSignalR("ChatHub connection already exists and is connected");
      return;
    }

    logSignalR("Proceeding with connection attempt...");

    try {
      // Get ChatHub URL from SocketService
      const hubUrl = await socketService.getChatHubUrl();
      logSignalR("Attempting to connect to ChatHub:", hubUrl);
      
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
          accessTokenFactory: () => {
            const token = Cookies.get("AccessToken");
            logSignalR("Using access token for ChatHub:", token ? "Token exists" : "No token");
            return token;
          },
        })
                            .configureLogging(signalR.LogLevel.None)
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .build();

      // Set server timeout and keep-alive interval
      newConnection.serverTimeoutInMilliseconds = 60000;
      newConnection.keepAliveIntervalInMilliseconds = 15000;

      // Start the connection
      logSignalR("Starting ChatHub connection...");
      await newConnection?.start();
      logSignalR("ChatHub connection started successfully");
      
      setConnection(newConnection);
      connectionRef.current = newConnection;

      // Start ping mechanism to keep connection alive
      startPingMechanism(newConnection);

      // Handle incoming messages
      newConnection?.on("UserConnected", (message) => {
        logSignalR("User connected:", message);
      });

      newConnection?.on("UserDisconnected", (message) => {
        logSignalR("User disconnected:", message);
      });

      newConnection?.on("ReceiveMessage", (senderId, message) => {
        logSignalR("Received message:", senderId, message);
      });

      newConnection?.on("Typing", (message) => {
        logSignalR("Typing indicator:", message);
      });

      // Handle connection state changes
      newConnection?.onreconnecting((error) => {
        logSignalR("ChatHub reconnecting:", error);
        clearInterval(pingIntervalRef.current);
      });

      newConnection?.onreconnected((connectionId) => {
        logSignalR("ChatHub reconnected:", connectionId);
        startPingMechanism(newConnection);
      });

      newConnection?.onclose(async (error) => {
        logSignalR("ChatHub connection closed:", error);
        logSignalR("Connection state before close:", newConnection?.state);
        logSignalR("Connection ID:", newConnection?.connectionId);
        
        clearInterval(pingIntervalRef.current);
        
        // Only handle 401 errors manually
        if (error && error.statusCode === 401) {
          try {
            logSignalR("Token expired, attempting to refresh...");
            await refreshToken();
            reconnectTimeoutRef.current = setTimeout(() => {
              logSignalR("Retrying ChatHub connection after token refresh...");
              startConnection();
            }, 2000);
          } catch (refreshError) {
            logSignalR("Error refreshing token:", refreshError);
            clearAuthCookies();
            navigate(ROUTES.LOGIN);
          }
        } else {
          logSignalR("Letting automatic reconnection handle the reconnection...");
        }
      });

    } catch (error) {
      logSignalR("Error starting ChatHub connection:", error);
      if (error.statusCode === 401) {
        try {
          logSignalR("Token expired during connection, attempting to refresh...");
          await refreshToken();
          reconnectTimeoutRef.current = setTimeout(() => {
            startConnection();
          }, 2000);
        } catch (refreshError) {
          logSignalR("Error refreshing token:", refreshError);
          clearAuthCookies();
          navigate(ROUTES.LOGIN);
        }
      } else {
        logSignalR("Connection failed, will retry in 30 seconds...");
        reconnectTimeoutRef.current = setTimeout(() => {
          startConnection();
        }, 30000);
      }
    }
  }, [startPingMechanism, navigate]);

  useEffect(() => {
    logSignalR("=== SignalRProvider useEffect triggered ===");
    logSignalR("Current connection state:", connection?.state);
    logSignalR("Access token exists:", !!Cookies.get("AccessToken"));
    
    // Only start connection if we don't have one or if it's not connected
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
      logSignalR("=== FORCING IMMEDIATE CONNECTION ===");
      startConnection();
    }
    
    // Also try with a delay as backup
    const timer = setTimeout(() => {
      if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
        logSignalR("=== DELAYED CONNECTION ATTEMPT ===");
        startConnection();
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [startConnection, connection]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      logSignalR("=== SignalRProvider cleanup ===");
      clearInterval(pingIntervalRef.current);
      clearTimeout(reconnectTimeoutRef.current);
      if (connectionRef?.current) {
        connectionRef?.current.stop();
      }
    };
  }, []);

  // Add a separate effect to monitor connection state
  useEffect(() => {
    if (connection) {
      logSignalR("ChatHub connection state changed:", connection.state);
      
      // If connection is connected, start ping mechanism
      if (connection.state === signalR.HubConnectionState.Connected) {
        logSignalR("ChatHub connected, starting ping mechanism...");
        startPingMechanism(connection);
      }
    }
  }, [connection, startPingMechanism]);

  return (
    <ChatContext.Provider value={connection}>
      {children}
    </ChatContext.Provider>
  );
};

export const useSignalR = () => useContext(ChatContext);